import { useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { useClientTranslation } from '@/lib/core/i18n';
import { ImagesToImageMap } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { HeartToggle } from '@/components/shared/shop/wishlist/ui/HeartToggle';
import { formatCardDateTime } from '@/components/shared/events/list/format';
import { isMembersOnly } from '@/components/shared/events/list/visibility';
import { useEventCategoryLabel } from '@/components/shared/events/list/categories';
import { allowsEventBooking, cardSeatsLeft, isCardSoldOut } from '@/components/shared/events/rules';
import type { FavoriteEvent } from '@/services/platform/data/events';

/**
 * Compact event row for the Events favorites list — the "ELP-card-horizontal" component in a
 * smaller variant: a fixed-width thumbnail a text content plus a "book now" / "add to
 * calendar" action inline, since there's no event detail screen behind it in this context
 * until the user taps through.
 */
export function FavoritesEventCard({
  favEvent,
  onRemove,
  onSelect,
}: {
  favEvent: FavoriteEvent;
  onRemove: () => void;
  onSelect: () => void;
}) {
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const { item, event } = favEvent;

  const title = event?.title ?? item.itemName;
  const imageUrl = event?.imageUrl ?? item.itemImageUrl;

  // Fall back to the CMS-configured no-pic image if the event has none or it fails to load;
  // the plain neutral placeholder is the last resort when even that setting is unconfigured.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [imageUrl]);
  const showImage = !!imageUrl && !imageFailed;
  const fallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  const membersOnly = isMembersOnly(event?.visibility);
  const isSoldOut = isCardSoldOut(event);
  const seatsLeft = cardSeatsLeft(event);
  const hasSeats = seatsLeft != null;
  const dateTime = formatCardDateTime(event?.startDateTime, locale, event?.endDateTime);

  return (
    <div className="border-jncquoi-neutral-200 flex flex-col gap-4 border-b-[0.5px] pb-4">
      {/* Whole row navigates to the event detail — only the heart (which stops propagation
          on its own click) opts out, matching the ELP card's tap target. */}
      <div className="flex w-full cursor-pointer items-start gap-4" onClick={onSelect}>
        <div className="relative h-[79px] w-16 shrink-0 overflow-hidden">
          {showImage ? (
            <img
              src={imageUrl}
              alt={title ?? ''}
              className="absolute inset-0 size-full object-cover"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : fallbackImageUrl ? (
            <img
              src={fallbackImageUrl}
              alt=""
              className="absolute inset-0 size-full object-cover"
              loading="lazy"
            />
          ) : (
            <span className="bg-jncquoi-neutral-100 absolute inset-0" aria-hidden="true" />
          )}
        </div>

        <div className="flex min-w-0 flex-1 items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {membersOnly && (
                  <>
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                      {t('App.Labels.MembersOnly')}
                    </span>
                    {event?.eventCategory && (
                      <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
                        ●
                      </span>
                    )}
                  </>
                )}
                {event?.eventCategory && (
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                    {getCategoryLabel(event.eventCategory)}
                  </span>
                )}
              </div>
              <h3 className="font-pp-neue-montreal text-jncquoi-neutral-1000 line-clamp-2 text-[16px] font-semibold tracking-[0.25px]">
                {title}
              </h3>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {event?.venueName && (
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] whitespace-nowrap">
                    {event.venueName}
                  </span>
                )}
                {event?.venueName && (isSoldOut || hasSeats) && (
                  <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
                    ●
                  </span>
                )}
                {isSoldOut ? (
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-600 text-[10px] font-semibold tracking-[0.5px] uppercase">
                    {t('App.Events.SoldOut')}
                  </span>
                ) : (
                  hasSeats && (
                    <div className="flex min-w-0 items-center gap-1">
                      <Icon name="user-badge-check" width={12} height={12} className="text-[#325d2d]" />
                      <span className="font-pp-neue-montreal text-[10px] font-semibold tracking-wide whitespace-nowrap text-[#325d2d] uppercase">
                        {t('App.Events.SeatsLeft', { count: seatsLeft ?? 0 })}
                      </span>
                    </div>
                  )
                )}
              </div>
              {dateTime && (
                <div className="flex min-w-0 items-center gap-1">
                  <Icon name="map-pin" width={12} height={12} className="text-jncquoi-neutral-700 shrink-0" />
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[12px] tracking-[0.5px]">
                    {dateTime}
                  </span>
                </div>
              )}
            </div>
          </div>

          <HeartToggle isFavorited onToggle={onRemove} size={24} className="shrink-0 pt-5" />
        </div>
      </div>

      <Button variant="secondary-outline" palette="dark" size="md" className="w-full" onClick={onSelect}>
        {allowsEventBooking(event) ? t('App.Events.BookNow') : t('App.Events.AddToCalendar')}
      </Button>
    </div>
  );
}
